import { useT } from '@kmgestao/i18n';
import { Button, PageSectionCard, Skeleton } from '@kmgestao/ui';

import {
  formatBRL,
  formatDateBR,
  useOwnNextInvoice,
  type OwnInvoiceRow,
} from '../../../features/clinic-billing/index.js';

export interface NextInvoiceCardProps {
  clinicId: string;
}

/**
 * Card "Próxima cobrança" — primeira fatura pendente (status
 * `pending`/`overdue`, `due_date asc`). Mesma query usada na
 * `PaymentPendingPage`, mas aqui sem polling (esta tela é informativa,
 * não bloqueante de acesso).
 *
 * Quando `invoice_url` existe, mostra botão direto pro link Asaas
 * (Pix/cartão/boleto). Owner pode pagar a qualquer momento, mesmo já
 * tendo acesso à plataforma.
 */
export function NextInvoiceCard({ clinicId }: NextInvoiceCardProps) {
  const { t } = useT('clinics');
  const invoiceQ = useOwnNextInvoice(clinicId);

  return (
    <PageSectionCard
      title={t('plan.nextInvoice.title')}
      description={t('plan.nextInvoice.description')}
      contentClassName="space-y-4"
    >
      {invoiceQ.isLoading ? (
        <NextInvoiceSkeleton />
      ) : invoiceQ.isError ? (
        <p className="text-sm text-destructive" role="alert">
          {t('plan.nextInvoice.loadError', {
            message: invoiceQ.error instanceof Error ? invoiceQ.error.message : '',
          })}
        </p>
      ) : invoiceQ.data ? (
        <InvoiceDetails invoice={invoiceQ.data} />
      ) : (
        <p className="text-sm text-muted-foreground">{t('plan.nextInvoice.empty')}</p>
      )}
    </PageSectionCard>
  );
}

function InvoiceDetails({ invoice }: { invoice: OwnInvoiceRow }) {
  const { t } = useT('clinics');
  const dueDate = formatDateBR(invoice.due_date);
  const isOverdue = invoice.status === 'overdue';
  return (
    <div className="space-y-4">
      <dl className="grid gap-4 sm:grid-cols-2">
        <div>
          <dt className="text-xs uppercase tracking-wider text-muted-foreground">
            {t('plan.nextInvoice.fields.amount')}
          </dt>
          <dd className="mt-1 text-2xl font-semibold text-foreground">
            {formatBRL(invoice.amount_brl)}
          </dd>
        </div>
        <div>
          <dt className="text-xs uppercase tracking-wider text-muted-foreground">
            {t('plan.nextInvoice.fields.dueDate')}
          </dt>
          <dd
            className={
              isOverdue
                ? 'mt-1 text-base font-medium text-destructive'
                : 'mt-1 text-base font-medium text-foreground'
            }
          >
            {dueDate ?? '—'}
            {isOverdue ? (
              <span className="ml-2 text-xs uppercase tracking-wider">
                {t('plan.nextInvoice.fields.overdueTag')}
              </span>
            ) : null}
          </dd>
        </div>
      </dl>

      {invoice.invoice_url ? (
        <div className="flex flex-wrap gap-2">
          <Button asChild size="lg">
            <a href={invoice.invoice_url} target="_blank" rel="noopener noreferrer">
              {t('plan.nextInvoice.actions.pay')}
            </a>
          </Button>
          {invoice.pdf_url ? (
            <Button asChild size="lg" variant="outline">
              <a href={invoice.pdf_url} target="_blank" rel="noopener noreferrer">
                {t('plan.nextInvoice.actions.downloadPdf')}
              </a>
            </Button>
          ) : null}
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">{t('plan.nextInvoice.noLink')}</p>
      )}
    </div>
  );
}

function NextInvoiceSkeleton() {
  return (
    <div className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Skeleton className="h-3 w-20" />
          <Skeleton className="h-7 w-32" />
        </div>
        <div className="space-y-2">
          <Skeleton className="h-3 w-24" />
          <Skeleton className="h-5 w-32" />
        </div>
      </div>
      <Skeleton className="h-11 w-40" />
    </div>
  );
}
